import './image-upload.scss';

import { App } from 'antd';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import TrashIcon from '@/media/icons/trash.svg?react';
import { convertFileToBase64 } from '@/shared/services/helpers/files';
import type { FileType } from '@/shared/types/files.types';

const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'];

interface IProps {
  title?: string;
  error?: string;
  file?: FileType;
  onChange: (file: Partial<FileType>) => void;
  onDelete: () => void;
  isRequired?: boolean;
  isFrozen?: boolean;
  allowedExtensions?: string[];
}

const ImageUpload: React.FC<IProps> = ({
  title,
  error,
  file,
  onChange,
  onDelete,
  isFrozen,
  isRequired,
  allowedExtensions = ALLOWED_IMAGE_EXTENSIONS
}) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChanged = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newFile = event.target.files?.[0];
    if (!newFile) return;

    const fileExtension = newFile.name.split('.').pop()?.toLowerCase();
    if (fileExtension && !allowedExtensions.includes(fileExtension)) {
      notification.error({
        message: t('ui.fileUpload.invalidExtensionTitle'),
        description: t('ui.fileUpload.invalidExtensionDescription', {
          extensions: allowedExtensions.join(', ')
        })
      });
      return;
    }

    try {
      const file = await convertFileToBase64(newFile);
      const base64 = file[1];
      const format = file[0];
      onChange({
        name: newFile.name,
        base64,
        size: (newFile.size * 4) / 3,
        format
      });
    } catch (error) {
      console.error(error);
      onChange({ name: undefined, base64: undefined, size: undefined });
    }
  };

  const isShowDelete = !isFrozen && file;

  return (
    <div className="image-upload">
      <label
        className={`image-upload__view ${error ? 'image-upload__view--error' : ''}`}
      >
        <input
          ref={inputRef}
          className="image-upload__field"
          type="file"
          accept={allowedExtensions
            .map((extension) => `.${extension}`)
            .join(', ')}
          onChange={handleFileChanged}
        />

        <p className="image-upload__title">
          {title || t('ui.imageUpload.addPhoto')}
          {isRequired && <span className="image-upload__require">*</span>}
        </p>

        {isShowDelete && (
          <button
            className="image-upload__delete"
            onClick={(e) => {
              e.preventDefault();
              if (inputRef.current) inputRef.current.value = '';
              onDelete();
            }}
          >
            <TrashIcon />
          </button>
        )}
      </label>

      {error && <p className="image-upload__error">{error}</p>}
    </div>
  );
};

export default ImageUpload;
